import React, { useState, useEffect, useMemo } from "react";
import { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {  faMapMarkerAlt, faGraduationCap } from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

const CoursesGrid = () => {
  const [apiData, setApiData] = useState(null);
  const swiperRef = useRef(null);

  // Fetch from WordPress API
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const res = await fetch("https://easylearne.com/wp-json/acf/v3/pages/7");
        const data = await res.json();
        setApiData(data?.acf || {});
      } catch (error) {
        console.error("Error fetching online universities:", error);
      }
    };

    fetchUniversities();
  }, []);

  // Prepare dynamic universities list
  const universities = useMemo(() => {
    if (!apiData) return [];

    const entries = [];

    Object.keys(apiData).forEach((key) => {
      if (key.startsWith("online_uni_image_")) {
        const index = key.split("_").pop();

        entries.push({
          id: index,
          img: apiData[`online_uni_image_${index}`],
          name: apiData[`online_uni_name_${index}`] || "University",
          location: apiData[`online_uni_location_${index}`] || "Not Provided",
          rating: apiData[`online_uni_rating_${index}`] || 4.5,
          likes: apiData[`online_uni_likes_${index}`] || 100,
          courses: (apiData[`online_uni_courses_${index}`] || "").split(",").map((c) => c.trim()),
          primaryTag: apiData[`online_uni_primary_tag_${index}`] || "Visit now",
          link: apiData[`online_uni_link_${index}`] || null,
        });
      }
    });

    return entries.sort((a, b) => Number(a.id) - Number(b.id));
  }, [apiData]);

  return (
    <section className="container py-14 relative">
      <div className=" px-4 sm:px-6 md:px-10">

        {/* Heading */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <FontAwesomeIcon icon={faGraduationCap} className="h-10 w-10 text-indigo-600" />
          <h2 className="text-3xl md:text-4xl font-bold">
            Domestic <span className="text-indigo-600">Offline</span> University
          </h2>
        </div>

       

       {/* Carousel */}
<Swiper
  modules={[Navigation, Autoplay]}
  onSwiper={(swiper) => (swiperRef.current = swiper)}

  autoplay={{
    delay: 2500,
    disableOnInteraction: false,
  }}

  loop={universities.length > 1}

  navigation={{
    nextEl: ".uni-next",
    prevEl: ".uni-prev",
  }}

  breakpoints={{
    320: { slidesPerView: 1, spaceBetween: 10 },
    600: { slidesPerView: 1.3, spaceBetween: 15 },
    768: { slidesPerView: 2, spaceBetween: 20 },
    1024: { slidesPerView: 3, spaceBetween: 22 },   // FIXED: was 4 (caused loop warning)
    1400: { slidesPerView: 4, spaceBetween: 25 },
  }}

  onInit={(swiper) => {
    // Re-bind navigation after DOM loads (WordPress issue)
    swiper.params.navigation.prevEl = ".uni-prev";
    swiper.params.navigation.nextEl = ".uni-next";
    swiper.navigation.init();
    swiper.navigation.update();
  }}

  onAfterInit={(swiper) => {
    // Refresh after slides load
    setTimeout(() => {
      swiper.update();
      swiper.loopCreate();
      swiper.navigation.update();
    }, 300);
  }}

  className="pb-6 px-6"
>
          {!apiData ? (
            <p className="text-center text-gray-500">Loading...</p>
          ) : universities.length === 0 ? (
            <p className="text-center text-red-500">No online universities added.</p>
          ) : (
            universities.map((uni) => (
              <SwiperSlide key={uni.id}>
                <div className="bg-white h-[330px] border border-gray-200 rounded-xl overflow-hidden hover:shadow-2xl transition">

                  {/* IMAGE */}
                  <div className="relative h-40 bg-cover bg-center" style={{ backgroundImage: `url(${uni.img})` }}>

                    {/* Small Tags */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 flex flex-wrap gap-1">
                      {uni.courses.map((course, i) =>
                        course !== uni.primaryTag ? (
                          <span key={i} className="text-[10px] px-2 py-0.5 bg-black/60 text-white rounded shadow-md">{course}</span>
                        ) : null
                      )}
                    </div>

                    {/* Primary Tag */}
                    <div className="absolute bottom-[-10px] left-10 right-10 h-12 bg-white rounded-t-[10px] flex justify-center pt-3">
                      {uni.link ? (
                          <a
                            href={uni.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-1 bg-green-600 text-white rounded-full text-xs font-bold shadow-md hover:bg-green-700 transition cursor-pointer"
                          >
                            {uni.primaryTag}
                          </a>
                        ) : (
                          <span className="px-4 py-1 bg-green-600 text-white rounded-full text-xs font-bold shadow-md">
                            {uni.primaryTag}
                          </span>
                        )}
                    </div>
                  </div>

                  {/* CONTENT */}
                  <div className="p-4 space-y-2 pt-6">
                    <h3 className="text-base font-bold text-gray-800 line-clamp-2">{uni.name}</h3>

                    <div className="flex items-start text-sm text-gray-600">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="h-4 w-4 mr-2 text-gray-400" />
                      <p className="line-clamp-2">{uni.location}</p>
                    </div>

                    {/* Ratings + Likes */}
                    {/* <div className="flex justify-between items-center pt-3">
                      <div className="flex items-center text-yellow-500">
                        <FontAwesomeIcon icon={faStar} className="h-3 w-3 mr-1" />
                        <span className="font-semibold">{uni.rating}</span>
                      </div>

                      <div className="flex items-center text-red-500 cursor-pointer hover:text-red-600">
                        <FontAwesomeIcon icon={faHeart} className="h-3 w-3 mr-1" />
                        <span>{uni.likes}</span>
                      </div>
                    </div> */}
                  </div>
                </div>
              </SwiperSlide>
            ))
          )}
        </Swiper>
         {/* Navigation Buttons */}
        <button className="uni-prev hidden md:flex items-center justify-center absolute -left-10 top-1/2 -translate-y-1/2 w-12 h-12 bg-white shadow-lg rounded-full text-gray-600 text-xl hover:scale-110 transition z-20">❮</button>
        <button className="uni-next hidden md:flex items-center justify-center absolute -right-10 top-1/2 -translate-y-1/2 w-12 h-12 bg-white shadow-lg rounded-full text-gray-600
        text-xl hover:scale-110 transition z-20">❯</button>
      </div>
      <div className="text-center pt-4">
       <button 
            className="
              bg-green-600 hover:bg-blue-700 
              text-white 
              transition 
              shadow-md rounded-full
              text-[clamp(0.8rem,1vw,1.1rem)] 
              px-6 py-2.5 sm:px-7 sm:py-3
            "
          ><a href="https://easylearne.com/domestic-offline-university/" >View More </a></button></div>
    </section>
  );
};

export default CoursesGrid;
